import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import MaxWidth from '@/components/common/layout/MaxWidth';
import { IAddress, IPageError } from '@/components/route/campground/campgroundTypes';
import ErrorPageComponent from '@/components/route/error/ErrorPage/ErrorPage';
import {
  About,
  AmenitiesAndSiteMap,
  BookAnRVSuite,
  HeaderActions,
  Hero,
  NearbyAttractions,
  NearbyParks,
} from '@/components/route/escapes';
import ReviewsContainer from '@/components/route/listing/Reviews/ReviewsContainer';
import { ESearchFilters } from '@/constants/searchFilters';
import { RV_CAMPSITE_BUNDLES_LANDING_PAGE } from '@/constants/urls';
import { useIsFirstRender } from '@/hooks/useIsFirstRender';
import { useNearbyParks } from '@/hooks/useNearbyParks';
import usePrevious from '@/hooks/usePrevious';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { EToastVariant, useToast } from '@/hooks/useToast';
import { getCampgroundAvailability } from '@/redux/modules/availability';
import { getBundledListings } from '@/redux/modules/bundledListings';
import { TRootState } from '@/redux/rootReducer';
import { getListingViewedEventData } from '@/redux/selectors/analytics-selectors';
import {
  getCampgroundListingData,
  getCampgroundListingId,
  getCampgroundPresentmentCurrency,
} from '@/redux/selectors/campgrounds/page';
import { getCampgroundAllReviews } from '@/redux/selectors/campgrounds/reviews';
import { getUserCurrency } from '@/redux/selectors/currency';
import { getCampgroundPrimaryImage, getRVBundleGallery } from '@/redux/selectors/listing/gallery';
import {
  trackListingQuoteUpdatedWithoutQuotesEvent,
  trackListingViewedEvent,
} from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { ERentalType } from '@/services/analytics/types';
import { getStateName, TCountry } from '@/utility/location';

import { STICKY_NAV_OFFSET } from '../route/listing/StickyNav/StickyNav';
import HeadMeta from '../static-pages/HeadMeta';

interface IProps {
  error?: IPageError | null;
}

const RvBundlesTemplate = ({ error }: IProps) => {
  const intl = useIntl();
  const router = useRouter();
  const campground = useSelector(getCampgroundListingData);
  const images = useSelector(getRVBundleGallery);
  const listingId = useSelector(getCampgroundListingId);
  const primaryImage = useSelector(getCampgroundPrimaryImage);
  const reviewsSummary = useSelector(getCampgroundAllReviews);
  const listingCurrency = useSelector(getCampgroundPresentmentCurrency);
  const userCurrency = useSelector(getUserCurrency);
  // TODO: Update this to use the previous URL of the escapes search page once we have it
  const listingViewedData = useSelector((state: TRootState) =>
    getListingViewedEventData(state, { searchUrl: router.asPath }),
  );
  const queryPath = router.asPath;
  const prevQueryPath = usePrevious(queryPath);
  const queryParams = useQueryParams();
  const prevQueryParams = usePrevious(router.query);

  const dispatch = useDispatch();
  const { showToast } = useToast();
  const searchFilterRef = useRef<HTMLDivElement>(null);
  const scrollToSearchFilter = useCallback(() => {
    const el = searchFilterRef?.current;
    if (el) {
      const offset = window.innerWidth >= 768 ? STICKY_NAV_OFFSET : 0;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;

      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  const { data: nearbyNationalParks } = useNearbyParks(
    campground?.geopoint?.lat,
    campground?.geopoint?.lon,
    'national',
  );

  const trackQuoteUpdate = useCallback(() => {
    const prevDatesFrom = prevQueryParams?.[ESearchFilters.DATE_FROM] || '';
    const prevDatesTo = prevQueryParams?.[ESearchFilters.DATE_TO] || '';
    const prevAdults = Number(prevQueryParams?.[ESearchFilters.GUESTS_ADULTS] || '0');
    const prevChildren = Number(prevQueryParams?.[ESearchFilters.GUESTS_CHILDREN] || '0');
    const prevInfants = Number(prevQueryParams?.[ESearchFilters.GUESTS_INFANTS] || '0');

    const newDatesFrom = queryParams[ESearchFilters.DATE_FROM] || '';
    const newDatesTo = queryParams[ESearchFilters.DATE_TO] || '';
    const newAdults = Number(queryParams[ESearchFilters.GUESTS_ADULTS] || '0');
    const newChildren = Number(queryParams[ESearchFilters.GUESTS_CHILDREN] || '0');
    const newInfants = Number(queryParams[ESearchFilters.GUESTS_INFANTS] || '0');

    const prevGuests = prevAdults + prevChildren + prevInfants;
    const newGuests = newAdults + newChildren + newInfants;

    if (prevDatesFrom !== newDatesFrom || newDatesTo !== prevDatesTo || prevGuests !== newGuests) {
      trackListingQuoteUpdatedWithoutQuotesEvent({
        prev: {
          dateFrom: prevDatesFrom as string,
          dateTo: prevDatesTo as string,
          guests: Number(prevGuests),
        },
        new: {
          dateFrom: newDatesFrom as string,
          dateTo: newDatesTo as string,
          guests: Number(newGuests),
        },
      });
    }
  }, [prevQueryParams, queryParams]);

  const isFirstRender = useIsFirstRender();
  // Get new rentals when user's currency changes and ensure reviews/addons are available
  useEffect(() => {
    const fetchData = async () => {
      const hasChangedCurrency = listingCurrency !== userCurrency;
      const hasChangedFilters = !!queryPath && !!prevQueryPath && queryPath !== prevQueryPath;

      if (listingId) {
        if (hasChangedCurrency || hasChangedFilters || isFirstRender) {
          trackQuoteUpdate();
          try {
            await dispatch(
              getBundledListings({
                ...queryParams,
              }),
            );
          } catch {
            showToast(
              EToastVariant.Error,
              intl.formatMessage({ defaultMessage: 'Oops', id: 'z/ipXX' }),
              intl.formatMessage({
                defaultMessage: 'Something went wrong, please try again.',
                id: '8GvVWZ',
              }),
            );
          }
        }
      }
    };

    fetchData();
  }, [
    listingId,
    dispatch,
    userCurrency,
    listingCurrency,
    queryPath,
    prevQueryPath,
    queryParams,
    showToast,
    intl,
    isFirstRender,
    trackQuoteUpdate,
  ]);

  useEffect(() => {
    if (campground?.id) {
      dispatch(getCampgroundAvailability(campground.id.toString()));
    }
  }, [campground?.id, dispatch]);

  useEffect(() => {
    if (!campground || !listingViewedData) return;
    trackListingViewedEvent({
      ...listingViewedData,
      source: EListingSource.VACATION_PACKAGES,
      rentalID: campground.id,
      rentalType: ERentalType.ESCAPE,
      isCampground: false,
      isEscape: true,
      campgroundAddress: `${campground.location.city}, ${campground.location.state}`,
      campgroundName: campground.name,
      campgroundCheckInTime: campground?.check_in_time,
      campgroundCheckOutTime: campground?.check_out_time,
    });
  }, [campground, listingViewedData]);

  if (error) {
    const { statusCode, message } = error;
    return <ErrorPageComponent statusCode={statusCode} message={message} />;
  }

  if (!campground) return null;

  const {
    name,
    location,
    description,
    filtered_description,
    filtered_name,
    geopoint,
    tagline,
    filtered_tagline,
    meta_title,
    meta_description,
  } = campground;

  const address: IAddress = {
    city: location.city,
    country: location.country,
    postal_code: location.zip,
    state: location.state,
    street: location.street,
  };

  const campgroundName = name || filtered_name;
  const campgroundTagline = tagline || filtered_tagline;
  const campgroundDescription = description || filtered_description;

  const pageTitle =
    meta_title ||
    intl.formatMessage(
      {
        defaultMessage: `Book Stress-Free RV Setups at {campground} - {locality}`,
        id: 'h3Qx0I',
      },
      {
        campground: campgroundName,
        locality: `${location.city}, ${location.state}`,
      },
    );

  const pageSEODescription =
    meta_description ||
    campgroundDescription.slice(0, 200) + (campgroundDescription.length > 200 ? '...' : '');

  // If this is the signature page standalone book-a-site experience, we do not want google to index
  // this as it is mostly empty, so we continue to supply the original pathname as the canonical URL.
  // We do NOT want to supply noindex=true as this will affect the actual listing page
  // See https://webmasters.stackexchange.com/questions/138977/use-noindex-for-url-with-parameters
  const canonicalUrl = router.asPath.split('?')[0] || '/';
  const thumbnailImgUrl = (primaryImage || images[0])?.url;

  const stateName =
    getStateName(location.state, location.country.toUpperCase() as TCountry) ?? location.state;
  const formattedLocation = `${location.city}, ${stateName}`;

  return (
    <>
      <HeadMeta
        title={pageTitle}
        description={pageSEODescription}
        canonicalUrl={canonicalUrl}
        url={canonicalUrl}
        thumbnailURL={thumbnailImgUrl}
      />

      <HeaderActions
        title={pageTitle}
        description={pageSEODescription}
        backUrl={RV_CAMPSITE_BUNDLES_LANDING_PAGE}
        campground={campground}
      />
      <Hero
        name={campgroundName}
        location={formattedLocation}
        images={images}
        scrollToSearchFilter={scrollToSearchFilter}
      />
      <About
        title={campgroundTagline || campgroundName}
        description={campgroundDescription}
        address={address}
        geopoint={geopoint}
      />
      <NearbyAttractions />

      <AmenitiesAndSiteMap siteMapImageURL={campground?.site_map_image_url || ''} />
      <BookAnRVSuite
        scrollToSearchFilter={scrollToSearchFilter}
        searchFilterRef={searchFilterRef}
      />

      <NearbyParks nearbyNationalParks={nearbyNationalParks} city={location.city} />

      {!!reviewsSummary?.reviews.length && (
        <section className="py-8 bg-white md:py-12">
          <MaxWidth className="mx-auto">
            <ReviewsContainer isCampground />
          </MaxWidth>
        </section>
      )}
    </>
  );
};

export default RvBundlesTemplate;
